import React, { useMemo } from 'react';
import { View, Pressable, StyleSheet, Platform } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import { useTranslation } from '@/hooks/useTranslation';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import useDeckAvailability from '@/hooks/useDeckAvailability';
import { useAuthStore } from '@/store';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { buildAppDashboardFooterSections, HARBOR_SPOTLIGHT } from '@/components/AppDashboardFooterNavData';
import AppDashboardFooterBrowsePills from '@/components/AppDashboardFooterBrowsePills';
import AppDashboardFooterBottomBar from '@/components/AppDashboardFooterBottomBar';
import PublicFooter from '@/components/PublicFooter';
import { safeAccessibilityRole } from '@/constants/utils/a11y';

const isWeb = Platform.OS === 'web';

/** Matches `decks/recipes/[slug].jsx` ScrollView horizontal padding — bleed footer edge-to-edge. */
const SCROLL_PAD_X = { xs: 16, md: 28 };

/** Neutral top wash (Variant A). */
const WASH = {
  light: ['rgba(15, 23, 42, 0.06)', 'rgba(15, 23, 42, 0.02)', 'transparent'],
  dark: ['rgba(248, 250, 252, 0.06)', 'rgba(248, 250, 252, 0.02)', 'transparent'],
};

/**
 * Per-breakpoint column count + fixed column width so partial last rows stay
 * left-aligned (no stretchy single-column row). Reflow: 5 → 4 → 3 → 2 → 1.
 */
function getColumnLayout({ isXS, isSM, isMD, isXL }) {
  if (isXS) return { cols: 1, width: '100%', columnGap: 0, rowGap: 24 };
  if (isSM) return { cols: 2, width: 220, columnGap: 28, rowGap: 28 };
  if (isMD) return { cols: 3, width: 220, columnGap: 32, rowGap: 32 };
  if (isXL) return { cols: 5, width: 200, columnGap: 32, rowGap: 36 };
  return { cols: 4, width: 220, columnGap: 32, rowGap: 32 };
}

function FooterLink({ icon, label, hint, onPress }) {
  const { colors } = useTheme();
  return (
    <Pressable onPress={onPress} style={isWeb ? { cursor: 'pointer' } : undefined}>
      {({ hovered, pressed }) => (
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'flex-start',
            gap: 12,
            paddingVertical: 9,
            paddingHorizontal: 8,
            marginHorizontal: -8,
            borderRadius: 10,
            opacity: pressed ? 0.72 : 1,
            ...(isWeb && hovered && {
              backgroundColor: `${colors.text_color_primary}10`,
            }),
          }}
        >
          <View style={{ width: 14, alignItems: 'center', paddingTop: 3 }}>
            <Icon
              name={icon}
              size={12}
              color={hovered ? colors.text_color_primary : colors.text_color_tertiary}
              solid
            />
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text
              size="s"
              weight="medium"
              numberOfLines={2}
              style={{
                color: colors.text_color_primary,
                letterSpacing: -0.15,
              }}
            >
              {label}
            </Text>
            {hint ? (
              <Text size="xxs" color="tertiary" style={{ marginTop: 3, lineHeight: 16 }} numberOfLines={2}>
                {hint}
              </Text>
            ) : null}
          </View>
          <View style={{ width: 12, alignItems: 'flex-end', paddingTop: 4 }}>
            <Icon
              name="arrow-right"
              size={9}
              color={hovered ? colors.text_color_secondary : colors.text_color_tertiary}
              style={{ opacity: hovered ? 1 : 0.4 }}
            />
          </View>
        </View>
      )}
    </Pressable>
  );
}

function FooterColumn({ title, children, width }) {
  const { colors } = useTheme();
  const isFull = width === '100%';
  return (
    <View
      style={{
        width,
        flexBasis: width,
        flexGrow: 0,
        flexShrink: isFull ? 1 : 0,
        gap: 2,
      }}
    >
      <Text
        size="xxs"
        weight="bold"
        style={{
          color: colors.text_color_tertiary,
          letterSpacing: 1.6,
          textTransform: 'uppercase',
          marginBottom: 10,
        }}
      >
        {title}
      </Text>
      {children}
    </View>
  );
}

/**
 * Signed-in footer: soft wash, lead copy, dashboard link columns, PublicFooter-style browse pills,
 * meta + Terms · Privacy. Falls back to the marketing `PublicFooter` for unauthenticated visitors.
 *
 * @param {object} props
 * @param {boolean} [props.fullViewportWidth] — when true, footer spans the scroll content width (no negative horizontal bleed, no inner max-width). Use when the footer sits outside a max-width page column.
 */
export default function AppDashboardFooter({ fullViewportWidth = false }) {
  const { colors, theme } = useTheme();
  const { isXS, isSM, isMD, isXL } = useResponsiveness();
  const deckAvailable = useDeckAvailability();
  const router = useRouter();
  const { t } = useTranslation();
  const isAuthenticated = useAuthStore(
    (state) => Boolean(state.token) && !state.isTokenExpired?.(),
  );
  const sections = useMemo(() => buildAppDashboardFooterSections()
    .filter(section => deckAvailable || !section.links.some(link => link.route.startsWith('/deck'))), [deckAvailable]);
  const browseItems = useMemo(() => HARBOR_SPOTLIGHT.filter(item => deckAvailable || !item.route.startsWith('/deck')), [deckAvailable]);

  if (!isAuthenticated) {
    return <PublicFooter />;
  }

  const bleed = isXS ? SCROLL_PAD_X.xs : SCROLL_PAD_X.md;
  const dark = theme === 'dark' || theme === 'oled';
  const washColors = dark ? WASH.dark : WASH.light;
  const layout = getColumnLayout({ isXS, isSM, isMD, isXL });

  const go = (path) => () => router.push(path);
  const onNavigate = (path) => router.push(path);

  return (
    <View
      style={[
        styles.outerBleed,
        {
          marginHorizontal: fullViewportWidth ? 0 : -bleed,
          paddingHorizontal: bleed,
          borderTopColor: `${colors.text_color_primary}12`,
        },
      ]}
      accessibilityRole={safeAccessibilityRole('contentinfo')}
    >
      <LinearGradient
        colors={washColors}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 0.75 }}
        style={StyleSheet.absoluteFill}
        pointerEvents="none"
      />

      <View style={fullViewportWidth ? styles.innerFullViewport : styles.inner}>
        <Text
          size="xxs"
          weight="bold"
          style={[styles.kicker, { color: colors.text_color_tertiary }]}
        >
          The whole workspace
        </Text>
        <Text
          weight="bold"
          color="primary"
          size="xxl"
          style={[styles.headline, isXS && styles.headlineXS]}
        >
          Every tool. One tap away.
        </Text>
        <Text size="s" color="secondary" style={styles.lede}>
          Models, bots, decks, files, memory — the complete index of where to go next, and how to get back.
        </Text>

        <View
          style={[
            styles.columns,
            { columnGap: layout.columnGap, rowGap: layout.rowGap },
          ]}
        >
          {sections.map((col) => (
            <FooterColumn key={col.title} title={col.title} width={layout.width}>
              {col.links.map((link) => (
                <FooterLink
                  key={`${link.route}-${link.label}`}
                  icon={link.icon}
                  label={link.label}
                  hint={link.hint}
                  onPress={go(link.route)}
                />
              ))}
            </FooterColumn>
          ))}
        </View>

        <AppDashboardFooterBrowsePills items={browseItems} onNavigate={onNavigate} />

        <AppDashboardFooterBottomBar
          metaText="Gab AI — multi-model workspace for teams and creators."
          termsLabel={t('common.termsOfService')}
          privacyLabel={t('common.privacyPolicy')}
          onNavigate={onNavigate}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  outerBleed: {
    alignSelf: 'stretch',
    width: '100%',
    marginTop: 48,
    paddingTop: 44,
    paddingBottom: 20,
    borderTopWidth: StyleSheet.hairlineWidth,
    overflow: 'hidden',
  },
  inner: {
    width: '100%',
    maxWidth: 1200,
    alignSelf: 'center',
  },
  innerFullViewport: {
    width: '100%',
    alignSelf: 'stretch',
  },
  kicker: {
    letterSpacing: 1.4,
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  headline: {
    marginBottom: 8,
    letterSpacing: -0.6,
    lineHeight: 32,
  },
  headlineXS: {
    lineHeight: 30,
  },
  lede: {
    lineHeight: 20,
    marginBottom: 24,
    maxWidth: 560,
    opacity: 0.9,
  },
  columns: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
});
